/** @file toRuntimePuppet: the bundle puppet as the authoring Puppet that evaluate/classify/place read. */
import type { ExportDrawable, ExportPuppet, ResolvedEntry } from '@zilo/pose3d-export/types/bundle';
import { SECTOR_TABLES } from '@zilo/pose3d-format/tables/sectorTables';
import type { Drawable } from '@zilo/pose3d-format/types/drawable';
import type { FacetEntry, SectorTable } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';

const cache = new WeakMap<ExportPuppet, Puppet>();

/**
 * A resolved entry without `rects`/`pivotPx`, at origin `[0, 0]`; a tile with a rect gets a slot (naming the entry's
 * sheet) so classify's `missing` agrees with the bundle; `applyBundleCells` then writes the real rect.
 */
function entryOf(entry: ResolvedEntry): FacetEntry {
  const rest: Partial<ResolvedEntry> = { ...entry };
  delete rest.rects;
  delete rest.pivotPx;
  const slots = Object.fromEntries(entry.rects.flatMap((r, t) => (r === null ? [] : [[String(t), entry.sheet]])));
  return { ...(rest as Omit<ResolvedEntry, 'rects' | 'pivotPx'>), origin: [0, 0], slots };
}

function drawableOf(d: ExportDrawable): Drawable {
  return d.kind === 'impostor' ? { ...d, facets: d.facets.map(entryOf) } : d;
}

/** The bundle's inlined tables minus the built-ins (a user table may not shadow one); built-ins resolve by id. */
function ownTables(tables: Record<string, SectorTable>): Record<string, SectorTable> {
  return Object.fromEntries(Object.entries(tables).filter(([id]) => !Object.hasOwn(SECTOR_TABLES, id)));
}

/**
 * The bundle puppet as a `pose3d/puppet@1` Puppet, memoised per bundle puppet object: sheets `{ image: file, size }`,
 * the inlined non-built-in tables as `sectorTables`, no `profileOverrides` (`profileData` is already effective),
 * impostor entries through `entryOf`; nodes, sockets, variants, bounds, facing and attachment metadata as they are.
 */
export function toRuntimePuppet(puppet: ExportPuppet): Puppet {
  const hit = cache.get(puppet);
  if (hit !== undefined) return hit;
  const { id, name, profile, facing, nodes, sockets, variants, bounds } = puppet;
  const runtime: Puppet = {
    format: 'pose3d/puppet@1', id, name, profile, facing, nodes, sockets, variants, bounds,
    drawables: puppet.drawables.map(drawableOf),
    sheets: Object.fromEntries(Object.entries(puppet.sheets).map(([key, s]) => [key, { image: s.file, size: [s.size[0], s.size[1]] }])),
    sectorTables: ownTables(puppet.tables),
    attachments: puppet.attachments,
  };
  cache.set(puppet, runtime);
  return runtime;
}
